import { useEffect, useRef, useState } from 'react';

import type { IIntegrationUIOption } from '@feathr/components';

interface ILazyOptionsArgs {
  /** Loads the options. Only called while `enabled` is true. */
  load: () => Promise<readonly IIntegrationUIOption[]>;
  /** Called with the error when a load fails, e.g. to show a toast. */
  onError: (error: unknown) => void;
  /** Whether the options are needed yet. */
  enabled: boolean;
}

/**
 * Select options loaded the first time `enabled` is true, so a request that needs a Blackbaud token
 * never runs before it can succeed. `undefined` while loading. A failed load gives `[]` and retries
 * the next time `enabled` turns true.
 */
export function useLazyOptions({
  enabled,
  load,
  onError,
}: ILazyOptionsArgs): readonly IIntegrationUIOption[] | undefined {
  const [options, setOptions] = useState<readonly IIntegrationUIOption[]>();
  const [hasFailed, setHasFailed] = useState(false);
  // The callbacks change identity every render; the effect should run for `enabled` only.
  const callbacks = useRef({ load, onError });
  callbacks.current = { load, onError };

  // `options` and `hasFailed` are left out on purpose: the effect sets them.
  useEffect(() => {
    if (!enabled || (options && !hasFailed)) {
      return;
    }
    // Ignore a response that lands after unmount or after `enabled` turns false.
    let isCurrent = true;

    async function loadOptions(): Promise<void> {
      try {
        const loaded = await callbacks.current.load();
        if (isCurrent) {
          setOptions(loaded);
          setHasFailed(false);
        }
      } catch (error) {
        if (isCurrent) {
          setOptions([]);
          setHasFailed(true);
          callbacks.current.onError(error);
        }
      }
    }

    void loadOptions();
    return (): void => {
      isCurrent = false;
    };
  }, [enabled]);

  return options;
}
